/**
 * Market Intelligence's data contracts (pivot doc §5, §6 Stage 3): the
 * competitors a tenant chooses to track, the competitions those competitors
 * run, and dated observations about them. Entry is manual or CSV import only
 * — there is no scraping or monitoring code. A `monitor` source exists in the
 * model for later; the UI represents it as "not enabled" until that code
 * exists and has been reviewed.
 */

import type { DrillDown } from '@/lib/drilldown'

export const COMPETITOR_FREQUENCIES = ['weekly', 'fortnightly', 'monthly', 'ad_hoc'] as const
export type CompetitorFrequency = (typeof COMPETITOR_FREQUENCIES)[number]

export const COMPETITOR_FREQUENCY_LABELS: Record<CompetitorFrequency, string> = {
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  monthly: 'Monthly',
  ad_hoc: 'Ad hoc',
}

export interface Competitor {
  id: string
  name: string
  domain: string
  brand: string
  /** Tracked competitions currently upcoming, live or closing soon. */
  activeCompetitions: number
  typicalTicketPrice: number
  frequency: CompetitorFrequency
  prizeCategories: string[]
  /** ISO date this competitor was first tracked. */
  trackedSince: string
  drillDown: DrillDown
}

export const TRACKED_COMPETITION_STATUSES = ['upcoming', 'live', 'closing_soon', 'closed'] as const
export type TrackedCompetitionStatus = (typeof TRACKED_COMPETITION_STATUSES)[number]

export const TRACKED_COMPETITION_STATUS_LABELS: Record<TrackedCompetitionStatus, string> = {
  upcoming: 'Upcoming',
  live: 'Live',
  closing_soon: 'Closing soon',
  closed: 'Closed',
}

export interface TrackedCompetition {
  id: string
  competitorId: string
  competitorName: string
  prize: string
  category: string
  ticketPrice: number
  maxEntries: number
  instantWins: number
  startDate: string
  endDate: string
  promotionalMessaging: string
  status: TrackedCompetitionStatus
  drillDown: DrillDown
}

export const OBSERVATION_SOURCES = ['manual', 'import', 'monitor'] as const
export type ObservationSource = (typeof OBSERVATION_SOURCES)[number]

export const OBSERVATION_SOURCE_LABELS: Record<ObservationSource, string> = {
  manual: 'Manual entry',
  import: 'CSV import',
  monitor: 'Automated monitoring',
}

export const OBSERVATION_CONFIDENCES = ['high', 'medium', 'low'] as const
export type ObservationConfidence = (typeof OBSERVATION_CONFIDENCES)[number]

/** What kind of thing an observation records — distinct from a tracked competition's prize category. */
export const ACTIVITY_TYPES = ['launch', 'closing_soon', 'price_change', 'category_trend', 'other'] as const
export type ActivityType = (typeof ACTIVITY_TYPES)[number]

export const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  launch: 'New launch',
  closing_soon: 'Closing soon',
  price_change: 'Price change',
  category_trend: 'Category trend',
  other: 'Other',
}

export interface MarketObservation {
  id: string
  competitorId: string | null
  competitorName: string | null
  trackedCompetitionId: string | null
  activityType: ActivityType
  fact: string
  occurredAt: string
  source: ObservationSource
  confidence: ObservationConfidence
  drillDown: DrillDown
}

export interface MarketActivitySummary {
  newLaunches: number
  closingSoon: number
  priceChanges: number
  categoryTrends: number
}

export interface CategoryPricePoint {
  category: string
  trackedCount: number
  averageTicketPrice: number
  averageMaxEntries: number
  instantWinShare: number
}

export interface MarketPricingSummary {
  averageTicketPrice: number
  averageMaxEntries: number
  /** Currently upcoming, live or closing-soon tracked competitions. */
  volume: number
  instantWinShare: number
  commonCategories: string[]
  promotionalPatterns: string[]
  byCategory: CategoryPricePoint[]
}

export interface PrizeTrendPoint {
  category: string
  trackedCount: number
  previousCount: number
  /** Fraction change vs the previous period; null when there is nothing to compare against. */
  change: number | null
  direction: 'rising' | 'falling' | 'steady'
}

export interface MonitoringStatus {
  enabled: false
  note: string
}

export interface CompetitorInput {
  name: string
  domain: string
  brand: string
  frequency: CompetitorFrequency
  typicalTicketPrice: number
  prizeCategories: string[]
}

export interface TrackedCompetitionInput {
  competitorId: string
  prize: string
  category: string
  ticketPrice: number
  maxEntries: number
  instantWins: number
  startDate: string
  endDate: string
  promotionalMessaging: string
}

export interface CsvImportResult {
  added: number
  errors: string[]
}
